import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppBreadcrumbs } from '../../components/navigation/AppBreadcrumbs';
import { brandColors } from '../../theme';
import { AppLink } from '../../components/navigation/AppLink';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { SEO } from '../../components/SEO';
import { generateBreadcrumbSchema } from '../../components/SEO/schemas';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
  Grid,
  Button,
  Chip,
  Fade,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useMarketGroupingDetails } from '../../hooks/useMarketCompassApi';
import { useCart } from '../../hooks/useCart';
import { SimplePriceSelector } from '../../components/cart/SimplePriceSelector';
import { SimpleAddToCartButton } from '../../components/cart/SimpleAddToCartButton';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

const MarketGroupingDetail: React.FC = () => {
  const navigate = useNavigate();
  const { hasStorePrivileged } = useUserPermissions();
  const { id } = useParams<{ id: string }>();
  const { addItem, isItemInCart } = useCart();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [selectedPrice, setSelectedPrice] = useState<{
    priceId: string;
    amount: number;
    interval: 'month' | 'year';
    productId: string;
  } | null>(null);

  // Fetch grouping details from API
  const groupingId = id ? parseInt(id) : undefined;
  const {
    data: groupingDetails,
    isLoading: loadingGrouping,
    error: groupingError
  } = useMarketGroupingDetails(groupingId);

  // Fetch products for this grouping
  const { data: productsData, isLoading: loadingProducts } = useQuery({
    queryKey: ['marketGroupingProducts', groupingId],
    queryFn: async () => {
      if (!groupingId) return null;
      const response = await authenticatedFetch(`${API_URL}/api/store/market-groupings/${groupingId}/products`);
      if (!response) throw new Error('No response from server');
      if (!response.ok) throw new Error('Failed to fetch products');
      const result = await response.json();
      return result.products || [];
    },
    enabled: !!groupingId,
  });

  // Filter markets by active status
  const filteredMarkets = useMemo(() => {
    if (!groupingDetails?.markets) {
      return [];
    }

    return groupingDetails.markets.filter(market => {
      // Filter out inactive markets for users without store.privileged permission
      if (!hasStorePrivileged && market.isActive === false) {
        return false;
      }
      return true;
    });
  }, [groupingDetails, hasStorePrivileged]);

  // Check if there are any inactive markets being displayed
  const hasInactiveMarkets = filteredMarkets.some(m => !m.isActive);

  // Get the first subscription product (if any) and transform prices
  const subscriptionProduct = useMemo(() => {
    console.log('productsData:', productsData);
    if (!productsData || productsData.length === 0) {
      console.log('No products data available');
      return null;
    }

    // Accept regular market grouping products OR grouping customization licenses
    const product = productsData.find((p: any) =>
      p.product_type === 'market_grouping' ||
      p.product_subtype === 'grouping_customization_license'
    );
    console.log('Found product:', product);
    console.log('Product prices from API:', product.prices);
    if (!product) {
      console.log('No matching product found. Products:', productsData.map((p: any) => ({ type: p.product_type, subtype: p.product_subtype })));
      return null;
    }

    // Transform prices to match SimplePriceSelector format
    const transformedPrices = product.prices?.map((p: any) => ({
      priceId: p.stripe_price_id || p.id,
      amount: p.unit_amount || p.amount,
      currency: p.currency || 'USD',
      interval: p.billing_interval === 'month' ? 'month' as const : 'year' as const,
    })) || [];

    console.log('Transformed prices:', transformedPrices);

    const result = {
      ...product,
      prices: transformedPrices,
      stripe_product_id: product.stripe_product_id || product.id,
    };
    console.log('Subscription product result:', result);

    return result;
  }, [productsData]);

  // Auto-select default price when products load (only needed for multiple price options)
  useEffect(() => {
    if (subscriptionProduct && subscriptionProduct.prices && subscriptionProduct.prices.length > 1) {
      const monthlyPrice = subscriptionProduct.prices.find((p: any) => p.interval === 'month');
      const priceToSelect = monthlyPrice || subscriptionProduct.prices[0];

      if (priceToSelect) {
        setSelectedPrice({
          priceId: priceToSelect.priceId || priceToSelect.stripe_price_id,
          amount: priceToSelect.amount || priceToSelect.unit_amount,
          interval: priceToSelect.interval,
          productId: subscriptionProduct.stripe_product_id,
        });
      }
    }
  }, [subscriptionProduct]);

  // Check if product is in cart
  const isProductInCart = () => {
    if (!subscriptionProduct || !subscriptionProduct.prices || subscriptionProduct.prices.length === 0) return false;

    // For single-price products, check the first price
    if (subscriptionProduct.prices.length === 1) {
      return isItemInCart(subscriptionProduct.prices[0].priceId);
    }

    // For multi-price products, check the selected price
    if (!selectedPrice) return false;
    return isItemInCart(selectedPrice.priceId);
  };

  // Loading state
  if (loadingGrouping) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading market grouping...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Error state
  if (groupingError || !groupingDetails) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">
            {groupingError?.message || 'Failed to load market grouping. Please try again.'}
          </Alert>
          <Button onClick={() => navigate('/global-market-reports')} sx={{ mt: 2 }}>
            Return to Global Market Reports
          </Button>
        </Box>
      </Container>
    );
  }

  // Breadcrumb schema for SEO
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Global Market Reports', url: '/global-market-reports' },
    { name: groupingDetails.name, url: `/market-groupings/${id}` }
  ]);

  return (
    <>
      <SEO
        title={`${groupingDetails.name} - Hotel Market Reports`}
        description={groupingDetails.description || `Access comprehensive hospitality market reports for ${groupingDetails.name}`}
        canonical={`/market-groupings/${id}`}
        structuredData={breadcrumbSchema}
      />
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          backgroundColor: brandColors.white,
        }}
      >
        <Container maxWidth="lg">
          {/* Breadcrumbs */}
          <AppBreadcrumbs
            items={[
              { label: 'Home', path: '/' },
              { label: 'Global Market Reports', path: '/global-market-reports' },
              { label: groupingDetails.name }
            ]}
          />

          {/* Show warning when inactive markets are displayed with special access */}
          {hasInactiveMarkets && hasStorePrivileged && (
            <Alert severity="warning" sx={{ mt: 4, mb: 4 }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                Special Access View
              </Typography>
              <Typography variant="body2">
                Some markets shown are flagged as Internal Only which means they are only visible to Horwath HTL licensees and not regular users.
              </Typography>
            </Alert>
          )}

          {/* Header */}
          <Box sx={{ mt: 4, mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: brandColors.indigo,
                mb: 2,
              }}
            >
              {groupingDetails.name}
            </Typography>
            {groupingDetails.description && (
              <Typography
                variant="body1"
                sx={{
                  color: brandColors.mediumGray,
                  mb: 3,
                }}
              >
                {groupingDetails.description}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{
                color: brandColors.mediumGray,
                fontWeight: 500,
              }}
            >
              {filteredMarkets.length} {filteredMarkets.length === 1 ? 'market' : 'markets'}
            </Typography>
          </Box>

          {/* Purchase Section */}
          {subscriptionProduct && subscriptionProduct.prices && subscriptionProduct.prices.length > 0 && (
            <Box
              sx={{
                mb: 4,
                p: 4,
                backgroundColor: 'rgba(32, 76, 229, 0.03)',
                borderRadius: 2,
                border: `1px solid ${brandColors.lightGray}`,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: brandColors.indigo,
                  mb: 1,
                }}
              >
                Subscribe to {groupingDetails.name}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: brandColors.mediumGray,
                  mb: 4,
                }}
              >
                Get access to comprehensive market reports and all the customization capability for all {filteredMarkets.length} markets in this grouping with a single subscription.
              </Typography>

              {/* Show price selector only if multiple prices, otherwise show static price */}
              {subscriptionProduct.prices.length > 1 ? (
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <SimplePriceSelector
                      prices={subscriptionProduct.prices}
                      onPriceSelect={(price: { priceId: string; amount: number; interval: 'month' | 'year' }) =>
                        setSelectedPrice({ ...price, productId: subscriptionProduct.stripe_product_id })
                      }
                      defaultInterval="month"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SimpleAddToCartButton
                      market={{
                        id: String(groupingId),
                        name: groupingDetails.name
                      }}
                      selectedPrice={selectedPrice || undefined}
                      size="large"
                      fullWidth
                      hidePrice={true}
                      isInCart={isProductInCart()}
                      productName={`${groupingDetails.name} Subscription`}
                      onAddToCart={(item) => addItem(item)}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: brandColors.mediumGray,
                          fontWeight: 600,
                          letterSpacing: 1,
                        }}
                      >
                        Monthly Subscription
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 0.5 }}>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 700,
                            color: brandColors.indigo,
                          }}
                        >
                          ${(subscriptionProduct.prices[0].amount / 100).toFixed(0)}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            ml: 1,
                            color: brandColors.mediumGray,
                            fontWeight: 400,
                          }}
                        >
                          / month
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <SimpleAddToCartButton
                      market={{
                        id: String(groupingId),
                        name: groupingDetails.name
                      }}
                      selectedPrice={{
                        priceId: subscriptionProduct.prices[0].priceId,
                        amount: subscriptionProduct.prices[0].amount,
                        interval: 'month',
                        productId: subscriptionProduct.stripe_product_id
                      }}
                      size="large"
                      fullWidth
                      hidePrice={true}
                      isInCart={isProductInCart()}
                      productName={`${groupingDetails.name} Subscription`}
                      onAddToCart={(item) => addItem(item)}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {/* Markets Grid */}
          {filteredMarkets.length === 0 ? (
            <Alert severity="info">
              No markets available
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {filteredMarkets.map((market) => (
                <Grid item xs={12} sm={6} md={4} key={market.id}>
                  <Fade in timeout={300}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        border: `1px solid ${brandColors.lightGray}`,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          borderColor: brandColors.blue,
                        },
                      }}
                    >
                      {!market.isActive && (
                        <Chip
                          label="Internal Only"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            backgroundColor: '#FFCC00',
                            color: 'black',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            zIndex: 1,
                          }}
                        />
                      )}
                      {/* Product Image */}
                      {market.product_image2 && (
                        <Box
                          component="img"
                          src={market.product_image2}
                          alt={market.name}
                          sx={{
                            width: '100%',
                            height: 180,
                            objectFit: 'cover',
                            borderTopLeftRadius: 4,
                            borderTopRightRadius: 4,
                          }}
                        />
                      )}
                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{
                            fontWeight: 600,
                            color: brandColors.indigo,
                            mb: 1.5,
                            lineHeight: 1.3,
                          }}
                        >
                          {market.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: brandColors.mediumGray,
                            mb: 0.5,
                          }}
                        >
                          {market.country.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: brandColors.mediumGray,
                          }}
                        >
                          {market.continent.name}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                        <Button
                          component={AppLink}
                          to={`/global-market-reports/${market.continent.slug}/${market.country.slug}/${market.slug}`}
                          size="small"
                          variant="outlined"
                          startIcon={<TrendingUpIcon />}
                          sx={{
                            borderColor: brandColors.blue,
                            color: brandColors.blue,
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: brandColors.blue,
                              backgroundColor: 'rgba(32, 76, 229, 0.05)',
                            },
                          }}
                        >
                          View Market
                        </Button>
                      </CardActions>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </>
  );
};

export default MarketGroupingDetail;
